/**
 * auditLogClient — soft-fail emit helper for client-side audit events.
 *
 * Posts to `/api/audit/log`, which appends via Admin SDK (Firestore rules
 * block direct client writes). Soft-fail: never throws, never blocks the
 * calling action.
 */
import type { AuditAction, AuditTarget } from '../types/auditLog';

export interface EmitAuditEventInput {
  action: AuditAction;
  target: AuditTarget;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

const ENDPOINT = '/api/audit/log';

export async function emitAuditEvent(input: EmitAuditEventInput): Promise<void> {
  try {
    const { authedFetch } = await import('./authedFetch');
    const res = await authedFetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok && res.status !== 404 && res.status !== 405) {
      // 404 / 405 means the endpoint isn't deployed yet — that's the
      // expected pre-Cloud-Function state and shouldn't spam the console.
      console.debug(`[auditLog] emit failed (${res.status}) for ${input.action}`);
    }
  } catch (e) {
    console.debug('[auditLog] emit threw:', (e as Error)?.message);
  }
}

/* ── Convenience wrappers ────────────────────────────────────────── */

export function emitDocumentInclude(
  reportId: string,
  docId: string,
  fileName?: string,
  metadata?: Record<string, unknown>,
): void {
  void emitAuditEvent({
    action: 'document.include',
    target: { type: 'document', id: docId, label: fileName },
    metadata: { reportId, ...metadata },
  });
}

export function emitDocumentExclude(
  reportId: string,
  docId: string,
  fileName?: string,
  reason?: string,
): void {
  void emitAuditEvent({
    action: 'document.exclude',
    target: { type: 'document', id: docId, label: fileName },
    metadata: { reportId, reason: reason ?? 'user-excluded' },
  });
}

export function emitDocumentPriorityToggle(
  siteId: string,
  docId: string,
  fileName: string | undefined,
  before: boolean,
  after: boolean,
): void {
  void emitAuditEvent({
    action: 'document.priority_toggle',
    target: { type: 'document', id: docId, label: fileName },
    before: { priority: before },
    after: { priority: after },
    metadata: { siteId },
  });
}

export function emitClassifyVersionBump(
  siteId: string,
  docCount: number,
  classifierVersion: string,
): void {
  void emitAuditEvent({
    action: 'document.classify_version_bump',
    target: { type: 'site', id: siteId },
    metadata: { docCount, classifierVersion },
  });
}

/* ── TSE-3 P3 — Template Studio audit helpers ─────────────────────── */

/**
 * Emit a `template.updated` audit event with the dot-path field-change
 * list pre-computed. Callers pass the pre + post snapshots and
 * `diffFieldPaths()` from `lib/templateDiff.ts` derives the changed
 * paths. `before` / `after` carry the full snapshots so the activity
 * tab can render side-by-side without a re-fetch.
 *
 * Mirrors the soft-fail discipline: never throws, never blocks Save.
 */
export function emitTemplateUpdated(input: {
  templateId: string;
  templateName?: string;
  fieldsChanged: string[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}): void {
  void emitAuditEvent({
    action: 'template.updated',
    target: { type: 'other', id: input.templateId, label: input.templateName },
    before: input.before,
    after: input.after,
    metadata: { fieldsChanged: input.fieldsChanged },
  });
}

export function emitTemplateForked(input: {
  sourceTemplateId: string;
  forkedTemplateId: string;
  templateName?: string;
}): void {
  void emitAuditEvent({
    action: 'template.forked',
    target: { type: 'other', id: input.forkedTemplateId, label: input.templateName },
    metadata: { sourceTemplateId: input.sourceTemplateId },
  });
}

export function emitTemplateDeleted(input: {
  templateId: string;
  templateName?: string;
  scope: 'company' | 'user';
}): void {
  void emitAuditEvent({
    action: 'template.deleted',
    target: { type: 'other', id: input.templateId, label: input.templateName },
    metadata: { scope: input.scope },
  });
}

export function emitTemplateVersionRestored(input: {
  templateId: string;
  templateName?: string;
  fromVersion: number;
  toVersion: number;
}): void {
  void emitAuditEvent({
    action: 'template.restored-version',
    target: { type: 'other', id: input.templateId, label: input.templateName },
    metadata: { fromVersion: input.fromVersion, toVersion: input.toVersion },
  });
}

export function emitThemeCommitted(input: {
  scope: 'user' | 'company';
  fieldsChanged: string[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  /** The template whose Save triggered this (global) theme commit. The theme is
   *  scope-global, but stamping the originating template lets the Studio's audit
   *  drawer show the commit tied to THIS template instead of every theme commit. */
  templateId?: string;
}): void {
  void emitAuditEvent({
    action: 'theme.committed',
    target: { type: 'other', id: `theme:${input.scope}` },
    before: input.before,
    after: input.after,
    metadata: { scope: input.scope, fieldsChanged: input.fieldsChanged, templateId: input.templateId },
  });
}

/**
 * UFL-5 — emit one event per rejected upload.
 *
 * Soft-fail: never throws, never blocks the surrounding UX (the user
 * already saw their toast). The Activity tab + admin "Upload
 * friction" rollup read these events to decide whether to raise the
 * 100 MB cap further or invest in chunked upload.
 *
 * `surface` distinguishes the call site (the doc library vs the
 * rerun wizard) so we can spot which path is causing the most pain.
 */
export function emitUploadRejected(input: {
  fileName: string;
  sizeBytes: number;
  reason: 'too-large' | 'zero-byte' | 'dotfile' | 'unsupported-ext';
  surface: 'document-library' | 'feasibility-rerun';
  siteId?: string;
  reportId?: string;
  ext?: string;
  capBytes?: number;
}): void {
  void emitAuditEvent({
    action: 'upload.rejected',
    target: { type: 'document', id: input.fileName, label: input.fileName },
    metadata: {
      reason: input.reason,
      sizeBytes: input.sizeBytes,
      surface: input.surface,
      siteId: input.siteId,
      reportId: input.reportId,
      ext: input.ext,
      capBytes: input.capBytes,
    },
  });
}

/* ── IIA review (IIA-FOLLOWUP-ADMIN-KPI-TILE) ─────────────────────── */

/**
 * Emit a per-image review decision from `ImageExtractionCard` so the
 * admin "Image extraction approval rate (7 days)" KPI can roll up
 * approved / amended / rejected counts without re-walking the claim
 * store.
 *
 * Best-effort: every action falls through `emitAuditEvent` which
 * swallows errors. The card never blocks the user-visible UI on the
 * emit, by design.
 */
export function emitIiaImageReview(input: {
  decision: 'approved' | 'amended' | 'rejected';
  docId: string;
  fileName?: string;
  kind?: string;
  confidence?: number;
  mode?: 'structured' | 'free-text' | 'awaiting';
  /** For `amended`, the patch shape (`'structured'` per-field /
   *  `'free-text'` observation). Omitted for approve/reject. */
  patchKind?: 'structured' | 'free-text';
  siteId?: string;
}): void {
  const action: 'iia.image.approved' | 'iia.image.amended' | 'iia.image.rejected' =
    input.decision === 'approved' ? 'iia.image.approved'
    : input.decision === 'amended' ? 'iia.image.amended'
    : 'iia.image.rejected';
  void emitAuditEvent({
    action,
    target: { type: 'document', id: input.docId, label: input.fileName },
    metadata: {
      kind: input.kind,
      confidence: input.confidence,
      mode: input.mode,
      patchKind: input.patchKind,
      siteId: input.siteId,
    },
  });
}
